import { useState, useEffect, useCallback } from "react";
import { Loader2, ArrowRight } from "lucide-react";
import { rollUp, summaryCells } from "./pilotStatus";

// WHERE THE PILOT STANDS, WITHOUT HUNTING FOR IT.
//
// The roster was correct and unreachable: one of nine tabs behind "Admin", with no sign from
// anywhere else in the app that it existed. This card is the signpost. It renders the same
// four numbers as the roster's own strip, from the same endpoint through the same reducer, and
// its only action is to open the roster.
//
// It reads the tracker itself rather than taking numbers as props, because the two places it
// appears (the CoFitter dashboard and Admin → Overview) have no state in common. Sharing the
// SOURCE is what keeps them agreeing; sharing a prop chain would only keep them coupled.

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

export default function PilotOptInCard({ onOpen, title = "HG Education pilot — opt-ins" }) {
  const [roll, setRoll] = useState(null);
  const [state, setState] = useState("loading"); // loading | ok | failed
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setState("loading");
    try {
      const res = await fetch(`${API_URL}/api/education/admin/tracker`, {
        credentials: "include",
        headers: { "Content-Type": "application/json" },
      });
      // A NON-200 IS "I DO NOT KNOW", NOT "NOBODY HAS OPTED IN". Falling through to the
      // rendered zeros would put four confident noughts on the dashboard the moment the
      // session expired, which reads as a pilot nobody answered.
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.detail || `Could not read the tracker (${res.status})`);
      }
      const summed = rollUp(await res.json());
      if (!summed) throw new Error("The tracker returned no summary to read.");
      setRoll(summed);
      setState("ok");
    } catch (e) {
      setError(e.message);
      setState("failed");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="optin-card" data-testid="pilot-optin-card">
      <div className="optin-card-head">
        <div>
          <div className="optin-card-title">{title}</div>
          <div className="optin-card-sub">
            {state === "ok"
              ? `${roll.invited} of ${roll.people} invited`
              : "Who has answered the invitation"}
          </div>
        </div>
        {onOpen && (
          <button className="btn btn-secondary optin-card-open" onClick={onOpen}
                  data-testid="pilot-optin-open">
            Open the opt-in tracker <ArrowRight size={14} />
          </button>
        )}
      </div>

      {state === "loading" && (
        <div className="optin-card-msg">
          <Loader2 size={15} className="pilot-spin" /> Reading the pilot&hellip;
        </div>
      )}

      {state === "failed" && (
        <div className="optin-card-msg optin-card-failed" data-testid="pilot-optin-error">
          {error} <button className="btn btn-text btn-sm" onClick={load}>Try again</button>
        </div>
      )}

      {state === "ok" && (
        <div className="optin-strip" data-testid="pilot-optin-strip">
          {summaryCells(roll).map((c) => (
            <div key={c.key} className={`optin-cell optin-cell-${c.tone}`}
                 data-testid={`optin-cell-${c.key}`}>
              <div className="optin-cell-value">{c.value}</div>
              <div className="optin-cell-label">{c.label}</div>
              {c.detail && <div className="optin-cell-detail">{c.detail}</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
